<template>
    <section class="forgot grid-center">
        <div class="forgot-box">
            <router-link to="/">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-x-lg close-icon"
                    viewBox="0 0 16 16"
                >
                    <path
                        d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"
                    />
                </svg>
            </router-link>
            <img src="@/assets/images/My Oxytocin Trademark.png" alt="My Oxytocin Trademark" class="trademark-logo" />
            
            <div v-if="emailSent" class="success-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="#10b981" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                </svg>
                <h3>Check Your Email</h3>
                <p>We've sent a password reset link to:</p>
                <p class="email-sent">{{ email }}</p>
                <p class="hint">Check your spam folder if you don't see it.</p>
                <router-link class="btn-back" to="/login">Back to Login</router-link>
            </div>
            
            <form v-else @submit.prevent="sendResetEmail">
                <p class="instruction">Enter your email address and we'll send you a link to reset your password.</p>
                <input 
                    type="email" 
                    v-model="email"
                    required 
                    placeholder="Email address"
                    :disabled="loading"
                />

                <action-button>
                    <button-preloader v-if="loading" />
                    <span v-else>Send Reset Link</span>
                </action-button>
                <router-link class="link" to="/login">Back to Login</router-link>
            </form>
        </div>
    </section>
</template>

<script>
import ActionButton from "@/components/ActionButton.vue";
import ButtonPreloader from "@/components/preloaders/ButtonPreloader.vue";
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from "@/firebase/init.js";

export default {
    components: { ActionButton, ButtonPreloader },
    name: "ForgotPassword",
    data() {
        return {
            email: "",
            loading: false,
            emailSent: false,
        };
    },
    methods: {
        async sendResetEmail() {
            if (!this.email.trim()) {
                this.$swal({
                    icon: 'warning',
                    title: 'Email Required',
                    text: 'Please enter your email address.',
                    confirmButtonColor: '#e37267',
                });
                return;
            }

            this.loading = true;

            try {
                await sendPasswordResetEmail(auth, this.email.trim());
                this.emailSent = true;
            } catch (error) {
                console.error('Password reset error:', error);
                
                let errorTitle = 'Error';
                let errorMessage = 'Failed to send reset email. Please try again.';
                
                switch (error.code) {
                    case 'auth/invalid-email':
                        errorTitle = 'Invalid Email';
                        errorMessage = 'Please enter a valid email address.';
                        break;
                    case 'auth/user-not-found':
                        // For security, don't reveal if email exists
                        // Still show success to prevent email enumeration
                        this.emailSent = true;
                        return;
                    case 'auth/too-many-requests':
                        errorTitle = 'Too Many Requests';
                        errorMessage = 'Please wait a few minutes before trying again.';
                        break;
                    case 'auth/network-request-failed':
                        errorTitle = 'No Connection';
                        errorMessage = 'Please check your internet connection.';
                        break;
                }
                
                this.$swal({
                    icon: 'error',
                    title: errorTitle,
                    text: errorMessage,
                    confirmButtonColor: '#e37267',
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.grid-center {
    display: grid;
    place-items: center;
}

.forgot {
    background-color: var(--grey-2);
    height: 100vh;
}

.forgot-box {
    text-align: center;
    background-color: whitesmoke;
    border-radius: 10px;
    padding: 50px 30px;
    min-width: 320px;
    max-width: 400px;
    margin-inline: auto;
    position: relative;
}

.close-icon {
    position: absolute;
    right: 20px;
    top: 20px;
    cursor: pointer;
    transition: color 0.2s;
}

.close-icon:hover {
    color: var(--dim-blue);
}

.trademark-logo {
    width: 200px;
    margin-bottom: 10px;
}

.forgot-box h3 {
    font-weight: 600;
    color: #333;
    margin: 15px 0 10px;
}

.forgot-box form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 20px;
}

.instruction {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
    margin-bottom: 5px;
}

.forgot-box input {
    width: 100%;
    padding: 12px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 15px;
    transition: border-color 0.2s;
}

.forgot-box input:focus {
    outline: none;
    border-color: #e37267;
}

.forgot-box input:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
}

.link {
    font-size: 15px;
    color: cornflowerblue;
}

.link:hover {
    color: var(--text);
}

/* Success state */
.success-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    padding: 20px 0;
}

.success-message h3 {
    color: #10b981;
    font-size: 22px;
    margin: 0;
}

.success-message p {
    margin: 0;
    color: #666;
    font-size: 14px;
}

.success-message .email-sent {
    font-weight: 600;
    color: #333;
    font-size: 16px;
}

.success-message .hint {
    font-size: 13px;
    color: #999;
    font-style: italic;
}

.btn-back {
    display: inline-block;
    margin-top: 15px;
    padding: 12px 30px;
    background: linear-gradient(135deg, #e37267 0%, #d4605a 100%);
    color: white;
    text-decoration: none;
    border-radius: 25px;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.3s;
}

.btn-back:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(227, 114, 103, 0.4);
}
</style>
